import { Link, useNavigate } from "react-router-dom";
import { MapPin, Heart, BadgeCheck, Star, Pin } from "lucide-react";
import { photoUrl, flyerUrl, showsFlyer } from "../lib/api";
import { ShareAdButton } from "./ShareAdButton";
import { StaffTag } from "./RoleAvatar";
import { priceLabel, PROVINCE_NAME, CATEGORY_LABEL, isIso } from "../lib/constants";
import { IsoBadge, IsoPlaceholder } from "./IsoBadge";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { catLabel } from "../lib/i18n";
import { api } from "../lib/api";
import { useState } from "react";
import { toast } from "sonner";
import { LostFoundBadge } from "./LostFoundBadge";
import { MissingDuration } from "./MissingDuration";
import { FeatureMenu, featureTimeLeft } from "./FeatureMenu";
import { LostCardActions } from "./LostCardActions";
import { favSavedToast } from "../lib/favorite";
import { LostFoundId } from "./LostFoundId";

export const ListingCard = ({ listing, onFavChange }) => {
  const { user, setAuthOpen } = useAuth();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [fav, setFav] = useState(listing.is_favorited);
  const story = listing.reunited_at ? listing.reunion_story : null;
  const flyer = !story && showsFlyer(listing);
  const img = flyer ? flyerUrl(listing, { w: 600, lang: lang === "fr" ? "fr" : "en" }) : photoUrl(story?.photo || (listing.photos || [])[0]);

  const price = listing.reunited_at ? t("lfr_back_home") : listing.lf_type ? t(listing.lf_type === "lost" ? "lf_tab_lost" : "lf_tab_found")
    : listing.price_type === "free"
    ? t("ld_free")
    : (listing.price_type === "contact" || listing.price == null)
      ? t("ld_contact_price")
      : priceLabel(listing);

  const toggleFav = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return setAuthOpen(true);
    try {
      if (fav) {
        await api.delete(`/favorites/${listing.id}`);
        setFav(false);
      } else {
        await api.post(`/favorites/${listing.id}`);
        favSavedToast(listing, lang);
        setFav(true);
      }
      onFavChange && onFavChange(listing.id, !fav);
    } catch {
      toast.error("Could not update favorite");
    }
  };

  const isStaff = user?.role === "admin" || user?.role === "moderator";
  const [feat, setFeat] = useState(!!listing.featured);
  const [sched, setSched] = useState(listing.featured_scheduled ? listing.featured_from : "");
  const [featUntil, setFeatUntil] = useState(listing.featured_until || "");
  const [featRenew, setFeatRenew] = useState(listing.feature_renew_days || null);
  const onFeatureChange = (d) => {
    const future = d.featured && d.featured_from && d.featured_from > new Date().toISOString();
    setFeat(d.featured && !future); setSched(future ? d.featured_from : ""); setFeatUntil(d.featured_until || ""); setFeatRenew(d.feature_renew_days || null);
  };

  return (
    <Link
      to={`/listing/${listing.id}`}
      data-testid={`listing-card-${listing.id}`}
      className={`group block bg-white rounded-2xl border overflow-hidden card-hover ${feat ? "border-[#F5551E] ring-1 ring-[#F5551E]/30" : "border-slate-200/80"}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
        {listing.reunited_at && (
          <div className="absolute bottom-5 -right-10 z-20 -rotate-45 bg-emerald-500 text-white text-[11px] font-extrabold tracking-widest px-10 py-1 shadow" data-testid={`reunited-ribbon-${listing.id}`}>
            {t("lf_reunited")}
          </div>
        )}
        {img ? (
          <img
            src={img}
            alt={listing.title}
            loading="lazy"
            data-testid={flyer ? `card-flyer-${listing.id}` : undefined}
            className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${flyer ? "object-top" : ""}`}
          />
        ) : isIso(listing) ? <IsoPlaceholder /> : (
          <div className="w-full h-full flex items-center justify-center text-slate-300 text-sm">{t("lc_no_photo")}</div>
        )}
        {isIso(listing) && <IsoBadge testid={`iso-badge-${listing.id}`} />}
        <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5 items-start">
          {listing.lf_type && !flyer && (
            <div className="flex gap-1.5">
              <LostFoundBadge listing={listing} />
              <MissingDuration listing={listing} />
            </div>
          )}
          {feat && (
            <span data-testid={`featured-badge-${listing.id}`} className="inline-flex items-center gap-1 bg-[#F5551E] text-white text-xs font-bold px-2.5 py-1 rounded-full shadow">
              <Star size={12} className="fill-white" /> {t("lc_featured")}
              {isStaff && featUntil && <span data-testid={`feature-countdown-${listing.id}`} className="font-semibold opacity-90">· {featureTimeLeft(featUntil)}{featRenew ? " ↻" : ""}</span>}
            </span>
          )}
          {listing.pinned && (
            <span data-testid={`pinned-badge-${listing.id}`} className="inline-flex items-center gap-1 bg-[#12234A]/90 backdrop-blur text-white text-xs font-semibold px-2.5 py-1 rounded-full shadow">
              <Pin size={11} className="fill-white" /> {t("lc_pinned")}
            </span>
          )}
        </div>
        {!flyer && (
          <span className={`absolute ${["top-3", "top-11", "top-[4.75rem]", "top-[6.5rem]"][(feat ? 1 : 0) + (listing.pinned ? 1 : 0) + (listing.lf_type ? 1 : 0)]} left-3 bg-white/95 backdrop-blur text-[#12234A] text-xs font-semibold px-2.5 py-1 rounded-full`}>
            {catLabel(lang, listing.category, CATEGORY_LABEL[listing.category] || listing.category)}
          </span>
        )}
        <div className={`absolute ${isIso(listing) ? "bottom-12" : "bottom-3"} left-3 z-10 flex flex-col items-start gap-1.5`}>
          {flyer && <MissingDuration listing={listing} />}
          {(listing.seller?.role === "admin" || listing.seller?.role === "moderator") && (
            <StaffTag role={listing.seller.role} className="shadow-sm" />
          )}
        </div>
        {listing.seller?.presence === "active" && !listing.reunited_at && (
          <span data-testid={`seller-active-${listing.id}`} title="This seller is active right now" className={`absolute ${flyer ? "bottom-14 right-3" : "bottom-3 right-3"} z-10 inline-flex items-center gap-1 bg-emerald-600 text-white text-[11px] font-semibold px-2 py-0.5 rounded-full shadow`}>
            <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" /> {t("lc_active_now")}
          </span>
        )}
        <button
          onClick={toggleFav}
          data-testid={`favorite-btn-${listing.id}`}
          aria-label="Save listing"
          className={`absolute ${flyer ? "bottom-3 right-3" : "top-3 right-3"} z-10 h-9 w-9 rounded-full bg-white/95 backdrop-blur flex items-center justify-center shadow-sm hover:scale-110 transition-transform`}
        >
          <Heart size={17} className={fav ? "fill-[#F5551E] text-[#F5551E]" : "text-slate-500"} />
        </button>
        {isStaff && <FeatureMenu listing={listing} feat={feat || !!sched} scheduled={sched} onChange={onFeatureChange} bottom={flyer} />}
        <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }} className={`absolute ${flyer ? "bottom-3" : "top-3"} ${isStaff ? "right-[5.75rem]" : "right-[3.25rem]"} z-10`}>
          <ShareAdButton listing={listing} size={16} testid={`card-share-${listing.id}`}
            className="h-9 w-9 rounded-full bg-white/95 backdrop-blur flex items-center justify-center shadow-sm hover:scale-110 transition-transform" />
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 data-testid="listing-card-title" className="font-display font-semibold text-[#12234A] leading-snug line-clamp-1">
            {listing.bilingual && listing.alt_lang === lang && listing.title_alt ? listing.title_alt : listing.title}
          </h3>
          <LostFoundId listing={listing} testid={`card-lf-id-${listing.id}`} />
        </div>
        <p data-testid="listing-card-price" className={`mt-1 text-lg font-bold font-display ${listing.reunited_at ? "text-emerald-600" : isIso(listing) ? "text-[#12234A]" : "text-[#F5551E]"}`}>
          {isIso(listing) ? (lang === "fr" ? "Recherché" : "Wanted") : price}
        </p>
        {story?.note && (
          <div className="mt-1.5 bg-emerald-50 border-l-2 border-emerald-500 rounded-r-lg px-2.5 py-1.5">
            <p data-testid={`reunion-quote-${listing.id}`} className="text-sm italic text-emerald-700 line-clamp-2">“{story.note}”</p>
          </div>
        )}
        <div className="mt-2 flex flex-wrap items-center justify-between gap-x-2 gap-y-1 text-sm text-slate-500">
          <span className="flex items-center gap-1 min-w-0 max-w-full">
            <MapPin size={14} className="shrink-0" />
            <span className="truncate">{listing.city}, {listing.province}</span>
            {listing.distance_km > 0 && <span className="shrink-0 text-xs font-semibold text-[#F5551E]" data-testid={`listing-distance-${listing.id}`}>· {listing.distance_km} km</span>}
          </span>
          {(listing.seller?.verified_business || listing.seller?.verified_seller || listing.seller?.is_verified_seller || listing.seller?.verified_breeder) && (
            <span className="flex items-center gap-1 shrink-0">
              {listing.seller?.verified_breeder && (
                <span className="flex items-center gap-1 bg-purple-600 text-white font-semibold px-2 py-0.5 rounded-full text-[11px]" data-testid="verified-breeder-badge" title={t("lc_verified_breeder")}>
                  <BadgeCheck size={13} className="text-white" /> {t("lc_verified_breeder")}
                </span>
              )}
              {listing.seller?.verified_business && (
                <span className="flex items-center gap-1 bg-amber-500 text-white font-semibold px-2 py-0.5 rounded-full text-[11px]" data-testid="verified-business-badge" title={t("lc_verified_business")}>
                  <BadgeCheck size={13} className="text-white" /> {t("lc_verified_business")}
                </span>
              )}
              {(listing.seller?.verified_seller || (!listing.seller?.verified_business && listing.seller?.is_verified_seller)) && (
                <span className="flex items-center gap-1 bg-emerald-600 text-white font-semibold px-2 py-0.5 rounded-full text-[11px]" data-testid="verified-badge" title={t("lc_verified_seller")}>
                  <BadgeCheck size={13} className="text-white" /> {t("lc_verified_seller")}
                </span>
              )}
            </span>
          )}
        </div>
        {listing.lf_type === "lost" && !listing.reunited_at && listing.status === "active" && <LostCardActions listing={listing} />}
      </div>
    </Link>
  );
};
